import {
  CallingStatusFilter,
  EngagementHistoryFilter,
  isSearchTreeLeafNode,
  isTemplatePlaceholder,
  MailingFilter,
  MembershipFilter,
  RosterFilter,
  SearchFilter,
  SearchTreeNode,
  TaskFilter,
} from '@/types';
import { getPlaceholderFiltersInLeafNode } from '@/util/searchTree/placeholderFilters';
import { placeholderFieldHash } from './placeholderTreeFind';

/**
 * Primary field is considered "empty" when it is an empty array or null/undefined,
 * and not a template placeholder. Used for compound filters whose primary is an array (membership, mailing, task).
 */
function primaryArrayEmptyAndNotPlaceholder(ids: unknown): boolean {
  return (Array.isArray(ids) ? ids.length === 0 : ids == null) && !isTemplatePlaceholder(ids);
}

/**
 * Primary field is considered "empty" when it is null or undefined, and not a template placeholder.
 * For SingleAutocompletes, we may have negative values like -1 to show "emptiness"
 * Used for compound filters whose primary is a single value (roster electionId).
 */
function primaryNullOrUndefinedAndNotPlaceholder(value: unknown): boolean {
  return (value == null || value === undefined || value === -1) && !isTemplatePlaceholder(value);
}

/**
 * For compound filters, secondary fields are hidden in the UI when the primary
 * field is empty and not a placeholder. This mapping defines, for each
 * secondary filterNameAndField, a predicate that returns true when that
 * secondary is "hidden" (primary empty and not placeholder).
 */
const SECONDARY_FIELD_HIDDEN_CHECKS: Record<string, (filter: SearchFilter) => boolean> = {
  'membership.branchIds': (filter) =>
    primaryArrayEmptyAndNotPlaceholder((filter as MembershipFilter).value?.statusIds),
  'membership.notRenewing': (filter) =>
    primaryArrayEmptyAndNotPlaceholder((filter as MembershipFilter).value?.statusIds),
  'mailing.mailingStatus': (filter) =>
    primaryArrayEmptyAndNotPlaceholder((filter as MailingFilter).value?.mailingIds),
  'task.kinds': (filter) =>
    primaryArrayEmptyAndNotPlaceholder((filter as TaskFilter).value?.electionIds),
  'task.choices': (filter) =>
    primaryArrayEmptyAndNotPlaceholder((filter as TaskFilter).value?.electionIds),
  'task.note': (filter) =>
    primaryArrayEmptyAndNotPlaceholder((filter as TaskFilter).value?.electionIds),
  'roster.electorateIds': (filter) =>
    primaryNullOrUndefinedAndNotPlaceholder((filter as RosterFilter).value?.electionId),
  'roster.rosterTypes': (filter) =>
    primaryNullOrUndefinedAndNotPlaceholder((filter as RosterFilter).value?.electionId),
  'roster.shiftStatus': (filter) =>
    primaryNullOrUndefinedAndNotPlaceholder((filter as RosterFilter).value?.electionId),
  'roster.votingPeriod': (filter) =>
    primaryNullOrUndefinedAndNotPlaceholder((filter as RosterFilter).value?.electionId),
  'callingStatus.callingStatusDateRange.displayedStartDate': (filter) =>
    primaryArrayEmptyAndNotPlaceholder((filter as CallingStatusFilter).value?.callingStatusLabels),
  'callingStatus.callingStatusDateRange.displayedEndDate': (filter) =>
    primaryArrayEmptyAndNotPlaceholder((filter as CallingStatusFilter).value?.callingStatusLabels),
  'callingStatus.callingStatusDateRange.callingPeriodQuantity': (filter) =>
    primaryArrayEmptyAndNotPlaceholder((filter as CallingStatusFilter).value?.callingStatusLabels),
  'callingStatus.callingStatusListIds': (filter) =>
    primaryArrayEmptyAndNotPlaceholder((filter as CallingStatusFilter).value?.callingStatusLabels),
  'callingStatus.callingStatusUserSubs': (filter) =>
    primaryArrayEmptyAndNotPlaceholder((filter as CallingStatusFilter).value?.callingStatusLabels),
  'callingStatus.callingStatusCampaignIds': (filter) =>
    primaryArrayEmptyAndNotPlaceholder((filter as CallingStatusFilter).value?.callingStatusLabels),
  'engagementHistory.activityDateRange.displayedStartDate': (filter) =>
    primaryArrayEmptyAndNotPlaceholder((filter as EngagementHistoryFilter).value?.activityIds),
  'engagementHistory.activityDateRange.displayedEndDate': (filter) =>
    primaryArrayEmptyAndNotPlaceholder((filter as EngagementHistoryFilter).value?.activityIds),
  'engagementHistory.activityDateRange.periodDetails': (filter) =>
    primaryArrayEmptyAndNotPlaceholder((filter as EngagementHistoryFilter).value?.activityIds),
  'engagementHistory.activityStatusIds': (filter) =>
    primaryArrayEmptyAndNotPlaceholder((filter as EngagementHistoryFilter).value?.activityIds),
  'engagementHistory.subject': (filter) =>
    primaryArrayEmptyAndNotPlaceholder((filter as EngagementHistoryFilter).value?.activityIds),
};

/**
 * Returns the set of placeholder hash keys (leafNodeId:filterNameAndField) for
 * placeholders whose field is hidden because the compound filter's primary
 * field is empty and not a placeholder.
 */
export function getHiddenPlaceholderKeys(searchTree: SearchTreeNode | undefined): Set<string> {
  const result = new Set<string>();
  if (!searchTree) return result;

  function walk(node: SearchTreeNode): void {
    if (isSearchTreeLeafNode(node)) {
      const leafNodeId = node._leafNodeId;
      if (!leafNodeId) return;
      const filter = node.filter as SearchFilter;
      if (!('value' in filter)) return;

      const placeholdersInNode = getPlaceholderFiltersInLeafNode(node);
      for (const { filterNameAndField } of placeholdersInNode) {
        const check = SECONDARY_FIELD_HIDDEN_CHECKS[filterNameAndField];
        if (check?.(filter)) {
          result.add(placeholderFieldHash(leafNodeId, filterNameAndField));
        }
      }
    } else {
      for (const child of node.nodes) {
        walk(child);
      }
    }
  }

  walk(searchTree);
  return result;
}
